import styled from "@emotion/styled";
import { useCallback } from "react";

import { Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { ElementStatePicker } from "./ElementStatePicker";
import type { ElementState, ElementStateOption } from "./types";
import { useActiveElementState, useSetActiveContainerElementState } from "./useActiveElementState";

const StyledElementStatePicker = styled(ElementStatePicker)`
  width: 70px; // this is a fixed width to prevent the Toolbar from resizing when the state changes.
  margin-left: 5px;
  height: auto;
  &:before,
  &:after {
    content: none; // disable border on the Select component.
  }
`;

type Props = {
  nodeId: number;
  allowedOptions: ElementStateOption[];
};

export const ToolbarElementStatePicker = ({ nodeId, allowedOptions }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const activeElementState = useActiveElementState();
  const setActiveContainerElementState = useSetActiveContainerElementState();

  const value = activeElementState?.nodeId === nodeId ? activeElementState.elementState : "default";

  const handleElementStateChange = useCallback(
    (elementState: ElementState) => setActiveContainerElementState(nodeId, elementState),
    [nodeId, setActiveContainerElementState],
  );

  if (!allowedOptions.length) {
    return null;
  }

  return (
    <Tooltip title={formatMessage("tooltip")} placement="top">
      <span>
        <StyledElementStatePicker
          label=""
          value={value}
          onChange={handleElementStateChange}
          allowedOptions={allowedOptions}
          SelectDisplayProps={{ style: { paddingBottom: "2px" } }}
        />
      </span>
    </Tooltip>
  );
};

const formatStrings = {
  tooltip: {
    en: "Select state to preview the element appearance when interacted with.",
  },
};
